import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DetalharIcon from '@mui/icons-material/OpenInNew';

import { DataTable, type DataTableAction, type DataTableColumn } from '../common';
import { oNivel, referencia, referenciaCurta } from '../risk-applications/textosDoDestino';
import { buscarEfetivos, type AplicacaoHerdada, type Efetivos } from '../../services/efetivosService';
import { NIVEIS_DE_APLICACAO, ROTULO_DO_NIVEL, type NivelDeAplicacao } from '../../types/nivelDeAplicacao';
import { CHAMADAS_SIMULTANEAS, executarComLimite } from './aplicarAosDestinos';
import { AcaoEmLink, LinkDeNavegacao } from './AcaoEmLink';
import { BarraDeCompletude } from './BarraDeCompletude';
import { completudeDe, rotuloDaJanela } from './completude';
import { colunaDoItem as montarColunaDoItem } from './colunaDoItem';
import { TODAS_AS_PERMISSOES, type PermissoesDaAplicacao } from './permissoesDaAplicacao';
import { DO_NIVEL, ESTE_NIVEL, contar, flexionar, juntarNomes, mesmoId } from './textosDoNivel';
import type {
  AcaoEmLote,
  CampoEditavel,
  ConfigDeAplicacao,
  DestinoRef,
  EstadoDaGravacao,
  ItemRecente,
  ValorDeCampo,
} from './tipos';
import { comecaEm, encerradaEm } from './vigencia';

export interface PainelDoDestinoProps<Item, A, V> {
  config: ConfigDeAplicacao<Item, A, V>;
  destino: DestinoRef;
  /** Fora da Plataforma (D13): vê, mas não edita, não seleciona e não remove. */
  somenteLeitura?: boolean;
  /** O que o perfil pode fazer; a ação que ele não pode some. Sem ela, tudo. */
  permissoes?: PermissoesDaAplicacao;
  /** Item que o endereço pediu para destacar (D14, `item`). */
  itemDestacado?: number | null;
  /** Avisado a cada releitura, para quem mostra contagens fora do painel. */
  aoCarregar?: (efetivos: Efetivos<A>) => void;
  /**
   * Uma lista só, com filtros pela origem (Todos / Da empresa / Do setor / Próprios): é como a
   * ficha mostra tudo o que vale para o destino, venha de onde vier. O item substituído aparece
   * nas duas linhas — a própria e a de cima, marcada como substituída. Sem ela, o painel é o da
   * tela de aplicação: os aplicados em cima e o que vem de cima num grupo recolhido.
   */
  listaUnica?: boolean;
  /** "Abrir na origem" de cada linha que vem de cima; null, ou ausente, esconde o link. */
  linkDaOrigem?: (herdada: AplicacaoHerdada<A>) => string | null;

  /** Inventário: o que vai logo acima da tabela — o resultado da inclusão e o aviso dela. */
  acimaDaTabela?: React.ReactNode;
  /** Itens recém-incluídos ou copiados de cima, o mais novo por último: sobem ao topo, com "Desfazer". */
  recentes?: ItemRecente[];
  /**
   * Onde guardar, por item, o id da aplicação que cada recente criou. O inventário passa o dele
   * para o "Desfazer" da inclusão em lote remover só essas; sem ele, o painel guarda para si.
   */
  registroDeCriadas?: Map<string, string>;
  /** A aplicação de um recente saiu ("Desfazer" ou "Remover"): ele deixa de ser recente. */
  aoDeixarDeSerRecente?: (itemId: number | string) => void;
  /** "Mudar só para o <destino>" de uma linha que vem de cima: cria a aplicação própria. */
  aoAjustar?: (herdada: AplicacaoHerdada<A>) => void;
  /** Itens cuja gravação está a caminho: o "Mudar só para" deles espera. */
  esperando?: (number | string)[];
  /** Muda quando algo foi gravado por fora (adicionar): o painel relê sem piscar. */
  versao?: number;
  /** Cada gravação daqui avisa, para o "Salvo às" ao lado do título. */
  aoMudarGravacao?: (estado: EstadoDaGravacao) => void;
}

/** O filtro de origem da lista única: tudo, só os próprios ou só os que vêm de um nível. */
type FiltroDeOrigem = 'todos' | 'proprios' | NivelDeAplicacao;

const DE_ORIGEM: Record<NivelDeAplicacao, string> = {
  company: 'Da empresa',
  unit: 'Da unidade',
  department: 'Do setor',
  occupation: 'Do cargo',
  employee: 'Do funcionário',
};

const NA_ORIGEM: Record<NivelDeAplicacao, string> = {
  company: 'na empresa',
  unit: 'na unidade',
  department: 'no setor',
  occupation: 'no cargo',
  employee: 'no funcionário',
};

/** O âncora de cada linha do inventário: a tela rola até ela (item do endereço, linhas novas). */
export function ancoraDoItem(familia: string, itemId: number | string): string {
  return `inventario-${familia}-${Number(itemId)}`;
}

/** O âncora da linha do mesmo item quando ele vem de cima: a própria, se houver, fica com o principal. */
export function ancoraDaHerdada(ancora: string): string {
  return `${ancora}-herdada`;
}

function capitalizar(texto: string): string {
  return texto.replace(/^./, (letra) => letra.toLocaleUpperCase('pt-BR'));
}

/** As cores das etiquetas de tipo, na ordem dos tipos da config. */
const CORES_DO_TIPO = ['warning', 'info', 'secondary', 'success', 'error'] as const;

function ehHerdada<A>(linha: A | AplicacaoHerdada<A>): linha is AplicacaoHerdada<A> {
  return typeof linha === 'object' && linha !== null && 'origem' in linha;
}

/** Uma linha que a edição mudou: o que o "Desfazer" confere e regrava. */
interface Mudanca {
  aplicacaoId: number | string;
  nome: string;
  anterior: ValorDeCampo;
  novo: ValorDeCampo;
}

interface Aviso<A> {
  mensagem: string;
  /** Uma mudança na edição na linha; várias numa ação em lote. */
  desfazer?: { campoId: string; mudancas: Mudanca[] };
  /** O que acabou de ser removido: o "Desfazer" recria com os mesmos dados. */
  recriar?: A;
}

interface EscolhaEmLote<A> {
  acao: AcaoEmLote<A>;
  valor: ValorDeCampo;
}

function iguais(a: ValorDeCampo, b: ValorDeCampo): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function escolhaVazia(valor: ValorDeCampo): boolean {
  return valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0);
}

/** O `gravar` das linhas só leitura: nada vai ao servidor. */
const naoGrava = async (): Promise<boolean> => false;

function motivo(erro: unknown, padrao: string): string {
  return erro instanceof Error && erro.message ? erro.message : padrao;
}

/**
 * O que vale para um destino (#606, D3/D5/D6): o que foi aplicado nele, editável na própria
 * linha, e o que vem dos níveis de cima, só leitura e com a origem. Usado pela tela de aplicação
 * e pelas fichas.
 *
 * Editar grava na hora, sem botão de salvar; o aviso traz "Desfazer", que relê antes de
 * regravar — se outra pessoa mudou o valor nesse meio-tempo, não passa por cima.
 */
export function PainelDoDestino<Item, A, V>({
  config,
  destino,
  somenteLeitura = false,
  permissoes = TODAS_AS_PERMISSOES,
  itemDestacado = null,
  aoCarregar,
  listaUnica = false,
  linkDaOrigem,
  acimaDaTabela,
  recentes = [],
  registroDeCriadas,
  aoDeixarDeSerRecente,
  aoAjustar,
  esperando = [],
  versao = 0,
  aoMudarGravacao,
}: PainelDoDestinoProps<Item, A, V>) {
  const [origemFiltrada, setOrigemFiltrada] = useState<FiltroDeOrigem>('todos');
  const [efetivos, setEfetivos] = useState<Efetivos<A> | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtroRapido, setFiltroRapido] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<(string | number)[]>([]);
  const [aRemover, setARemover] = useState<A[] | null>(null);
  const [removendo, setRemovendo] = useState(false);
  const [aviso, setAviso] = useState<Aviso<A> | null>(null);
  const [emDetalhe, setEmDetalhe] = useState<A | null>(null);
  // Salvar pela janela é conferir: a aplicação deixa de mostrar "confira", mesmo depois de reler.
  // A chave é a aplicação, não o item — removido e incluído de novo, o item volta a pedir conferência.
  const [conferidasNaJanela, setConferidasNaJanela] = useState<ReadonlySet<string>>(() => new Set());
  const [emLote, setEmLote] = useState<EscolhaEmLote<A> | null>(null);
  const [gravandoLote, setGravandoLote] = useState(false);
  const { familia } = config;
  const podeIncluir = !somenteLeitura && permissoes.incluir;
  const podeEditar = !somenteLeitura && permissoes.editar;
  const podeRemover = !somenteLeitura && permissoes.remover;
  // Por ref: quem passa uma função nova a cada render não deve disparar outra leitura.
  const aoCarregarRef = useRef(aoCarregar);
  aoCarregarRef.current = aoCarregar;
  const aoMudarGravacaoRef = useRef(aoMudarGravacao);
  aoMudarGravacaoRef.current = aoMudarGravacao;
  /** A aplicação que cada item recém-adicionado criou: o "Desfazer" só remove essa. */
  const [registroProprio] = useState(() => new Map<string, string>());
  const criadaPorItem = registroDeCriadas ?? registroProprio;
  const recenteDe = (aplicacao: A) =>
    recentes.find((recente) => mesmoId(recente.itemId, config.aplicacao.itemId(aplicacao)));

  const reler = useCallback(async (): Promise<Efetivos<A> | null> => {
    try {
      const lidos = await buscarEfetivos<A>(familia, destino.nivel, destino.id);
      setEfetivos(lidos);
      setErro(null);
      aoCarregarRef.current?.(lidos);
      return lidos;
    } catch (falha: unknown) {
      setErro(motivo(falha, 'Não foi possível carregar o que vale para este destino.'));
      return null;
    }
  }, [familia, destino.nivel, destino.id]);

  useEffect(() => {
    setEfetivos(null);
    setSelecionados([]);
    void reler();
  }, [reler]);

  // Gravado por fora: relê mantendo a lista à vista, sem o "carregando".
  const versaoLida = useRef(versao);
  useEffect(() => {
    if (versaoLida.current === versao) return;
    versaoLida.current = versao;
    void reler();
  }, [versao, reler]);

  /** Envolve uma gravação: avisa "Salvando…", depois "Salvo às" ou o erro. */
  const gravando = useCallback(async <T,>(gravacao: () => Promise<T>, padraoDeErro: string): Promise<T> => {
    aoMudarGravacaoRef.current?.({ tipo: 'salvando' });
    try {
      const resultado = await gravacao();
      aoMudarGravacaoRef.current?.({ tipo: 'salvo', em: new Date() });
      return resultado;
    } catch (falha: unknown) {
      aoMudarGravacaoRef.current?.({ tipo: 'erro', mensagem: motivo(falha, padraoDeErro) });
      throw falha;
    }
  }, []);

  const nomeDe = config.aplicacao.nome;
  const termo = busca.trim().toLocaleLowerCase('pt-BR');
  const filtrar = useCallback(
    <T extends A>(lista: T[]): T[] =>
      lista.filter((aplicacao) => {
        if (termo && !nomeDe(aplicacao).toLocaleLowerCase('pt-BR').includes(termo)) return false;
        const filtro = config.filtrosRapidos?.find((f) => f.id === filtroRapido);
        return filtro ? filtro.aceita(aplicacao) : true;
      }),
    [termo, nomeDe, config.filtrosRapidos, filtroRapido]
  );

  const proprias = useMemo(() => filtrar(efetivos?.proprias ?? []), [efetivos, filtrar]);
  const herdadas = useMemo(() => filtrar(efetivos?.herdadas ?? []), [efetivos, filtrar]);

  // Cada recém-adicionado fica preso à aplicação que criou, vista na primeira leitura depois dele.
  // O que deixou de ser recente solta a aplicação: adicionado de novo, prende a nova.
  useEffect(() => {
    const chaves = new Set(recentes.map((recente) => String(Number(recente.itemId))));
    for (const chave of [...criadaPorItem.keys()]) {
      if (!chaves.has(chave)) criadaPorItem.delete(chave);
    }
    for (const { itemId } of recentes) {
      const chave = String(Number(itemId));
      if (criadaPorItem.has(chave)) continue;
      const criada = efetivos?.proprias.find((a) => mesmoId(config.aplicacao.itemId(a), itemId));
      if (criada) criadaPorItem.set(chave, String(config.aplicacao.id(criada)));
    }
  }, [recentes, efetivos, config.aplicacao, criadaPorItem]);

  const gravar = useCallback(
    async (aplicacao: A, campo: CampoEditavel<A>, novo: ValorDeCampo): Promise<boolean> => {
      const anterior = campo.ler(aplicacao);
      if (iguais(anterior, novo)) return false;
      try {
        await gravando(() => config.editar(destino, aplicacao, campo.id, novo), 'Não foi possível gravar a mudança.');
      } catch (falha: unknown) {
        setAviso({ mensagem: motivo(falha, 'Não foi possível gravar a mudança.') });
        return false;
      }
      const lidos = await reler();
      const alcance = lidos?.alcance ?? efetivos?.alcance ?? { funcionarios: 0 };
      setAviso({
        mensagem:
          config.descreverEdicao?.(aplicacao, campo, anterior, novo, alcance) ??
          `${campo.rotulo} de ${nomeDe(aplicacao)} mudou.`,
        desfazer: {
          campoId: campo.id,
          mudancas: [{ aplicacaoId: config.aplicacao.id(aplicacao), nome: nomeDe(aplicacao), anterior, novo }],
        },
      });
      return true;
    },
    [config, destino, reler, efetivos, nomeDe, gravando]
  );

  /**
   * Regrava o valor anterior só nas linhas que ainda têm o valor gravado — relido agora. A que
   * outra pessoa mudou nesse meio-tempo fica como está, e o aviso diz qual.
   */
  const desfazer = useCallback(async () => {
    const pedido = aviso?.desfazer;
    setAviso(null);
    if (!pedido) return;
    const campo = config.camposEditaveis.find((c) => c.id === pedido.campoId);
    const lidos = await reler();
    const mudaramDepois: string[] = [];
    const naoDesfeitas: string[] = [];
    let desfeitas = 0;
    await executarComLimite(
      pedido.mudancas.map((mudanca) => async () => {
        const atual = lidos?.proprias.find((a) => mesmoId(config.aplicacao.id(a), mudanca.aplicacaoId));
        if (!atual || !campo || !iguais(campo.ler(atual), mudanca.novo)) {
          mudaramDepois.push(mudanca.nome);
          return;
        }
        try {
          await gravando(() => config.editar(destino, atual, campo.id, mudanca.anterior), 'Não foi possível desfazer.');
          desfeitas += 1;
        } catch (falha: unknown) {
          naoDesfeitas.push(`${mudanca.nome} (${motivo(falha, 'não foi possível desfazer')})`);
        }
      }),
      CHAMADAS_SIMULTANEAS
    );
    if (desfeitas > 0) await reler();

    if (pedido.mudancas.length === 1) {
      const [falhou] = naoDesfeitas;
      setAviso({
        mensagem:
          desfeitas === 1 ? 'Desfeito.' : falhou ? `Não foi possível desfazer: ${falhou}.` : 'Mudou depois da sua edição; não desfeito.',
      });
      return;
    }
    const partes: string[] = [];
    if (desfeitas > 0) partes.push(`Desfeito em ${contar(desfeitas, config.rotulos.singular, config.rotulos.plural)}`);
    if (mudaramDepois.length > 0) {
      const um = mudaramDepois.length === 1;
      partes.push(
        `${juntarNomes(mudaramDepois)} ${um ? 'mudou' : 'mudaram'} depois da sua edição e não ${um ? 'foi desfeito' : 'foram desfeitos'}`
      );
    }
    if (naoDesfeitas.length > 0) partes.push(`não foi possível desfazer ${naoDesfeitas.join(', ')}`);
    setAviso({ mensagem: `${partes.join('; ')}.` });
  }, [aviso, reler, config, destino, gravando]);

  /**
   * "Desfazer" de um recém-adicionado: relê e remove a aplicação que ele criou, só se ela ainda
   * existe — se alguém já a removeu e aplicou de novo, a nova fica.
   */
  const desfazerAdicao = async (aplicacao: A) => {
    const itemId = config.aplicacao.itemId(aplicacao);
    const criada = criadaPorItem.get(String(Number(itemId))) ?? String(config.aplicacao.id(aplicacao));
    const lidos = await reler();
    const atual = lidos?.proprias.find((a) => mesmoId(config.aplicacao.id(a), criada));
    if (!atual) {
      setAviso({ mensagem: `${nomeDe(aplicacao)} mudou depois de adicionado; não desfeito.` });
      return;
    }
    try {
      await gravando(() => config.remover(destino, [atual]), 'Não foi possível desfazer.');
    } catch (falha: unknown) {
      setAviso({ mensagem: `Não foi possível desfazer: ${motivo(falha, 'tente de novo')}.` });
      return;
    }
    const ajustado = recenteDe(aplicacao)?.como === 'ajustado';
    criadaPorItem.delete(String(Number(itemId)));
    aoDeixarDeSerRecente?.(itemId);
    await reler();
    setAviso({
      mensagem: ajustado
        ? `${nomeDe(aplicacao)} volta a seguir o que vem de cima.`
        : `${nomeDe(aplicacao)} saiu de ${destino.nome}.`,
    });
  };

  const deCimaDe = useCallback(
    (removidas: A[]) =>
      (efetivos?.herdadas ?? []).filter((herdada) =>
        removidas.some((removida) => mesmoId(config.aplicacao.itemId(herdada), config.aplicacao.itemId(removida)))
      ),
    [efetivos, config.aplicacao]
  );

  const confirmarRemocao = async () => {
    if (!aRemover) return;
    setRemovendo(true);
    try {
      await gravando(() => config.remover(destino, aRemover), 'Não foi possível remover.');
      // Removido, deixa de ser recente: adicionado de novo, o "Desfazer" vale para a aplicação nova.
      for (const removida of aRemover) {
        const itemId = config.aplicacao.itemId(removida);
        criadaPorItem.delete(String(Number(itemId)));
        if (recenteDe(removida)) aoDeixarDeSerRecente?.(itemId);
      }
      const [unica] = aRemover;
      setAviso(
        aRemover.length === 1
          ? {
              mensagem: `${nomeDe(unica)} removido ${referenciaCurta('de', destino.nivel, destino.nome)}.`,
              recriar: listaUnica ? undefined : unica,
            }
          : { mensagem: `${aRemover.length} removidos.` }
      );
      setSelecionados([]);
      setARemover(null);
      await reler();
    } catch (falha: unknown) {
      setAviso({ mensagem: motivo(falha, 'Não foi possível remover.') });
    } finally {
      setRemovendo(false);
    }
  };

  /**
   * "Desfazer" da remoção: recria a aplicação com os mesmos dados que ela tinha — a mesma cópia
   * que "Mudar só para" faz do que vem de cima, agora a partir da que foi removida.
   */
  const desfazerRemocao = async (removida: A) => {
    setAviso(null);
    const comoHerdada: AplicacaoHerdada<A> = {
      ...removida,
      origem: { nivel: destino.nivel, id: destino.id, nome: destino.nome },
      substituidaNoDestino: false,
    };
    try {
      const resposta = await gravando(() => config.copiarDoHerdado(destino, comoHerdada), 'Não foi possível desfazer.');
      const [falha] = resposta.falharam;
      if (falha) throw new Error(falha.motivo);
    } catch (falha: unknown) {
      setAviso({ mensagem: `Não foi possível desfazer: ${motivo(falha, 'tente de novo')}.` });
      return;
    }
    await reler();
    setAviso({ mensagem: `${nomeDe(removida)} voltou ${referenciaCurta('a', destino.nivel, destino.nome)}.` });
  };

  const selecionadas = (efetivos?.proprias ?? []).filter((a) =>
    selecionados.some((id) => mesmoId(id, config.aplicacao.id(a)))
  );
  /**
   * O valor escolhido vai a cada linha marcada pelo mesmo `editar` da edição na linha, três por
   * vez. Linha que já tinha o valor não é regravada; a que falha é dita pelo nome; só as que
   * mudaram entram no "Desfazer".
   */
  const executarEmLote = async ({ acao, valor }: EscolhaEmLote<A>) => {
    const campo = config.camposEditaveis.find((c) => c.id === acao.campo);
    if (!campo) return;
    setGravandoLote(true);
    const planejadas = selecionadas.map((aplicacao) => ({
      aplicacao,
      anterior: campo.ler(aplicacao),
      novo: acao.valorPara ? acao.valorPara(aplicacao, valor) : valor,
    }));
    const aGravar = planejadas.filter(({ anterior, novo }) => !iguais(anterior, novo));
    const gravadas: Mudanca[] = [];
    const falhas: string[] = [];
    await executarComLimite(
      aGravar.map(({ aplicacao, anterior, novo }) => async () => {
        try {
          await gravando(() => config.editar(destino, aplicacao, campo.id, novo), 'Não foi possível gravar.');
          gravadas.push({ aplicacaoId: config.aplicacao.id(aplicacao), nome: nomeDe(aplicacao), anterior, novo });
        } catch (falha: unknown) {
          falhas.push(`${nomeDe(aplicacao)} (${motivo(falha, 'não foi possível gravar')})`);
        }
      }),
      CHAMADAS_SIMULTANEAS
    );
    setGravandoLote(false);
    setEmLote(null);
    setSelecionados([]);
    await reler();

    const { singular, plural } = config.rotulos;
    const jaEstavam = planejadas.length - aGravar.length;
    const partes: string[] = [];
    if (gravadas.length > 0) partes.push(`${contar(gravadas.length, singular, plural)} ${gravadas.length === 1 ? 'mudou' : 'mudaram'}`);
    if (jaEstavam > 0) partes.push(`${contar(jaEstavam, singular, plural)} já ${jaEstavam === 1 ? 'estava' : 'estavam'} assim`);
    if (falhas.length > 0) partes.push(`não gravou em ${falhas.join(', ')}`);
    setAviso({
      mensagem: `${acao.rotulo}: ${partes.join('; ')}.`,
      desfazer: gravadas.length > 0 ? { campoId: campo.id, mudancas: gravadas } : undefined,
    });
  };

  /** O de cima que a própria substituiu: de onde ela veio, quando foi copiada. */
  const substituidaPor = (aplicacao: A) =>
    (efetivos?.herdadas ?? []).find(
      (herdada) => herdada.substituidaNoDestino && mesmoId(config.aplicacao.itemId(herdada), config.aplicacao.itemId(aplicacao))
    );
  const destinoCurto = referenciaCurta('', destino.nivel, destino.nome);
  // A linha recém-incluída fica verde e a copiada de cima, azul; o texto diz o mesmo a quem não
  // distingue a cor.
  const notaDoItem = (aplicacao: A) => {
    const doRecente = ehHerdada(aplicacao) ? undefined : recenteDe(aplicacao);
    if (!doRecente) return null;
    if (doRecente.como === 'ajustado') {
      const origem = substituidaPor(aplicacao)?.origem.nivel;
      const deCima = origem ? oNivel(origem) : 'o que vem de cima';
      return (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="info.dark">
            {`Agora vale só para ${destinoCurto} o que você escolher aqui. ${capitalizar(deCima)} continua igual.`}
          </Typography>
          {podeRemover && (
            <AcaoEmLink onClick={() => void desfazerAdicao(aplicacao)}>{`Voltar a seguir ${deCima}`}</AcaoEmLink>
          )}
        </Box>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="body2" color="success.dark" fontWeight={600}>
          {listaUnica ? 'Adicionado agora ·' : 'Incluído agora ·'}
        </Typography>
        {podeRemover && (
          <AcaoEmLink
            cor="success.dark"
            aria-label={`Desfazer a inclusão de ${nomeDe(aplicacao)}`}
            onClick={() => void desfazerAdicao(aplicacao)}
          >
            Desfazer
          </AcaoEmLink>
        )}
      </Stack>
    );
  };
  const marcasDoItem = (aplicacao: A) => {
    const periodo = config.aplicacao.periodo?.(aplicacao);
    const fim = periodo ? encerradaEm(periodo) : null;
    const inicio = periodo ? comecaEm(periodo) : null;
    const destacado = itemDestacado !== null && mesmoId(config.aplicacao.itemId(aplicacao), itemDestacado);
    const tipo = config.aplicacao.tipo?.(aplicacao) ?? null;
    const indiceDoTipo = config.tipos.findIndex((t) => t.valor === tipo);
    const ancora = listaUnica ? undefined : ancoraDoItem(familia, config.aplicacao.itemId(aplicacao));
    const herdada = ehHerdada(aplicacao);
    return (
      <>
        {ancora && <span id={herdada ? ancoraDaHerdada(ancora) : ancora} data-herdada={herdada || undefined} />}
        {indiceDoTipo >= 0 && (
          <Chip
            size="small"
            variant="outlined"
            color={CORES_DO_TIPO[indiceDoTipo % CORES_DO_TIPO.length]}
            label={config.tipos[indiceDoTipo].rotulo}
          />
        )}
        {destacado && <Chip size="small" color="info" label="Você veio por este item" />}
        {fim && <Chip size="small" variant="outlined" label={`encerrada em ${fim}`} />}
        {inicio && <Chip size="small" variant="outlined" label={`começa em ${inicio}`} />}
      </>
    );
  };
  const conteudoDoItem = { nome: nomeDe, descricao: config.aplicacao.descricao, marcas: marcasDoItem, nota: notaDoItem };
  const colunaDoItem = montarColunaDoItem<A>(config.rotulos.singular, conteudoDoItem);
  // No inventário, a completude fica abaixo do nome e da descrição, onde o olho já está lendo o item.
  const colunaDoItemComCompletude = montarColunaDoItem<A>(config.rotulos.singular, {
    ...conteudoDoItem,
    nota: (aplicacao: A) => {
      const completude = completudeDe(aplicacao);
      return (
        <>
          {notaDoItem(aplicacao)}
          {completude && (
            <Box sx={{ mt: 0.5 }}>
              <BarraDeCompletude completude={completude} />
            </Box>
          )}
        </>
      );
    },
  });

  const JanelaDeDetalhe = config.JanelaDeDetalhe;
  const acoesDasProprias: DataTableAction<A>[] = [
    ...(JanelaDeDetalhe
      ? [{ icon: <DetalharIcon fontSize="small" />, tooltip: 'Ficha', onClick: (a: A) => setEmDetalhe(a) }]
      : []),
    ...(!podeRemover
      ? []
      : [
          {
            icon: <DeleteIcon fontSize="small" />,
            tooltip: 'Remover',
            color: 'error' as const,
            onClick: (a: A) => setARemover([a]),
          },
        ]),
  ];

  const origemDaHerdada = (herdada: AplicacaoHerdada<A>) => {
    const link = linkDaOrigem?.(herdada) ?? null;
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        <Typography variant="body2">
          Vem de {ROTULO_DO_NIVEL[herdada.origem.nivel]} {herdada.origem.nome}
        </Typography>
        {herdada.substituidaNoDestino && <Chip size="small" variant="outlined" label="substituído neste destino" />}
        {herdada.convocacaoSegue && (
          <Typography variant="body2" color="text.secondary">
            a convocação segue o de {ROTULO_DO_NIVEL[herdada.convocacaoSegue.nivel]} {herdada.convocacaoSegue.nome}
          </Typography>
        )}
        {link && (
          <LinkDeNavegacao to={link}>Abrir {NA_ORIGEM[herdada.origem.nivel]}</LinkDeNavegacao>
        )}
      </Stack>
    );
  };

  const esteDestino = ESTE_NIVEL[destino.nivel];
  const emEspera = (herdada: AplicacaoHerdada<A>) =>
    esperando.some((id) => mesmoId(id, config.aplicacao.itemId(herdada)));
  const mudarSoPara = `Mudar só para ${destinoCurto}`;

  const filtrosAtivos = termo !== '' || filtroRapido !== null;
  const limparFiltros = () => {
    setBusca('');
    setFiltroRapido(null);
    setOrigemFiltrada('todos');
  };

  // Lista única (ficha): as próprias, editáveis, e as de cima, só leitura, na mesma tabela.
  type Linha = A | AplicacaoHerdada<A>;
  const idDaLinha = (linha: Linha): string =>
    ehHerdada(linha)
      ? `herdada-${linha.origem.nivel}-${String(config.aplicacao.id(linha))}`
      : String(config.aplicacao.id(linha));
  const degrauDoDestino = NIVEIS_DE_APLICACAO.indexOf(destino.nivel);
  const herdadasDoNivel = (nivel: NivelDeAplicacao) =>
    (efetivos?.herdadas ?? []).filter((herdada) => herdada.origem.nivel === nivel);
  // Unidade é opcional: o filtro só aparece quando alguma vem dela.
  const niveisDeOrigem = NIVEIS_DE_APLICACAO.filter(
    (nivel, degrau) => degrau < degrauDoDestino && (nivel !== 'unit' || herdadasDoNivel(nivel).length > 0)
  );
  const filtrosDeOrigem: { id: FiltroDeOrigem; rotulo: string; quantos: number }[] = [
    { id: 'todos', rotulo: 'Todos', quantos: (efetivos?.proprias.length ?? 0) + (efetivos?.herdadas.length ?? 0) },
    ...niveisDeOrigem.map((nivel) => ({ id: nivel, rotulo: DE_ORIGEM[nivel], quantos: herdadasDoNivel(nivel).length })),
    { id: 'proprios', rotulo: 'Próprios', quantos: efetivos?.proprias.length ?? 0 },
  ];
  let linhasDaLista: Linha[] = [...proprias, ...herdadas];
  if (origemFiltrada === 'proprios') linhasDaLista = proprias;
  else if (origemFiltrada !== 'todos') linhasDaLista = herdadas.filter((h) => h.origem.nivel === origemFiltrada);

  const colunasDaLista: DataTableColumn<Linha>[] = [
    colunaDoItem as DataTableColumn<Linha>,
    ...config.camposEditaveis.map(
      (campo): DataTableColumn<Linha> => ({
        id: campo.id,
        label: campo.rotulo,
        render: (linha) =>
          ehHerdada(linha)
            ? campo.render(linha, { gravar: naoGrava, somenteLeitura: true, efetivos, destino })
            : campo.render(linha, { gravar: (novo) => gravar(linha, campo, novo), somenteLeitura: !podeEditar, efetivos, destino }),
      })
    ),
    {
      id: 'origem',
      label: 'Vem de',
      render: (linha) =>
        ehHerdada(linha) ? (
          origemDaHerdada(linha)
        ) : (
          <Typography variant="body2">Aplicado a {esteDestino}</Typography>
        ),
    },
  ];
  const acoesDaLista: DataTableAction<Linha>[] = acoesDasProprias.map((acao) => ({
    ...acao,
    onClick: (linha: Linha) => {
      if (!ehHerdada(linha)) acao.onClick(linha);
    },
    hidden: (linha: Linha) => ehHerdada(linha),
  }));
  const idsDasProprias = new Set((efetivos?.proprias ?? []).map((a) => String(config.aplicacao.id(a))));

  const acoesDaSelecao = (
    <Stack direction="row" spacing={1}>
      {podeEditar &&
        config.acoesEmLote?.map((acao) => (
        <Button key={acao.id} size="small" variant="outlined" onClick={() => setEmLote({ acao, valor: acao.valorInicial })}>
          {acao.rotulo}
        </Button>
      ))}
      {podeRemover && (
        <Button size="small" color="error" variant="outlined" onClick={() => setARemover(selecionadas)}>
          Remover selecionados
        </Button>
      )}
    </Stack>
  );

  // Inventário: os recém-incluídos sobem ao topo, o mais novo primeiro; depois as próprias e, por
  // fim, o que vem de cima e ainda vale aqui. O substituído some: a própria já diz de onde veio.
  const ordemDeRecente = (aplicacao: A) =>
    recentes.findIndex((recente) => mesmoId(recente.itemId, config.aplicacao.itemId(aplicacao)));
  const propriasNaTela = [...(efetivos?.proprias ?? [])].sort((a, b) => ordemDeRecente(b) - ordemDeRecente(a));
  const herdadasQueValem = (efetivos?.herdadas ?? []).filter((herdada) => !herdada.substituidaNoDestino);
  const linhasDoInventario: Linha[] = [...propriasNaTela, ...herdadasQueValem];
  const f = (palavra: string) => flexionar(config.rotulos.feminino, palavra);

  const origemDaLinha = (linha: Linha) => {
    if (ehHerdada(linha)) {
      return (
        <Box>
          <Typography variant="body2">{`Vem ${DO_NIVEL[linha.origem.nivel]} ${linha.origem.nome}`}</Typography>
          {linha.convocacaoSegue && (
            <Typography variant="body2" color="text.secondary">
              a convocação segue o de {ROTULO_DO_NIVEL[linha.convocacaoSegue.nivel]} {linha.convocacaoSegue.nome}
            </Typography>
          )}
        </Box>
      );
    }
    const substituida = substituidaPor(linha);
    return (
      <Box>
        <Typography variant="body2">{capitalizar(`d${esteDestino}`)}</Typography>
        {substituida && (
          <Typography variant="body2" color="text.secondary">
            {`antes vinha ${DO_NIVEL[substituida.origem.nivel]}`}
          </Typography>
        )}
      </Box>
    );
  };

  const acoesDaLinha = (linha: Linha) => {
    const nome = nomeDe(linha);
    if (ehHerdada(linha)) {
      const link = linkDaOrigem?.(linha) ?? null;
      return (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
          {aoAjustar && podeIncluir && (
            <Button
              size="small"
              variant="outlined"
              aria-label={`${mudarSoPara}: ${nome}`}
              disabled={emEspera(linha)}
              onClick={() => aoAjustar(linha)}
            >
              {emEspera(linha) ? 'Copiando…' : mudarSoPara}
            </Button>
          )}
          {link && (
            <LinkDeNavegacao to={link}>Abrir {NA_ORIGEM[linha.origem.nivel]}</LinkDeNavegacao>
          )}
        </Stack>
      );
    }
    const completude = completudeDe(linha);
    const rotuloDoLink = rotuloDaJanela(completude);
    return (
      <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        {JanelaDeDetalhe && (
          <AcaoEmLink aria-label={`${rotuloDoLink} de ${nome}`} onClick={() => setEmDetalhe(linha)}>
            {rotuloDoLink}
          </AcaoEmLink>
        )}
        {podeRemover && (
          <AcaoEmLink cor="error.main" aria-label={`Remover ${nome}`} onClick={() => setARemover([linha])}>
            Remover
          </AcaoEmLink>
        )}
      </Stack>
    );
  };

  // Item 30% e ações 25%; origem e os campos editáveis dividem o resto por igual.
  const larguraDoMeio = `${Number((45 / (1 + config.camposEditaveis.length)).toFixed(2))}%`;
  const colunasDoInventario: DataTableColumn<Linha>[] = [
    { ...(colunaDoItemComCompletude as DataTableColumn<Linha>), width: '30%' },
    { id: 'origem', label: 'Origem', width: larguraDoMeio, render: origemDaLinha },
    ...config.camposEditaveis.map(
      (campo): DataTableColumn<Linha> => ({
        id: campo.id,
        label: campo.rotulo,
        width: larguraDoMeio,
        render: (linha) =>
          ehHerdada(linha)
            ? campo.render(linha, { gravar: naoGrava, somenteLeitura: true, efetivos, destino })
            : campo.render(linha, {
                gravar: (novo) => gravar(linha, campo, novo),
                somenteLeitura: !podeEditar,
                recente: recenteDe(linha)?.como === 'adicionado' && !conferidasNaJanela.has(String(config.aplicacao.id(linha))),
                efetivos,
                destino,
              }),
      })
    ),
    { id: 'acoes', label: 'Ações', align: 'right', width: '25%', render: acoesDaLinha },
  ];

  const destinoLido = efetivos?.destino;
  const caminhoDoDestino =
    destinoLido && (destinoLido.nivel === 'company' ? destinoLido.empresaNome : `${destinoLido.empresaNome} › ${destino.nome}`);

  const removidaDoAviso = aviso?.recriar;
  let acaoDoAviso: React.ReactNode;
  if (aviso?.desfazer) {
    acaoDoAviso = (
      <AcaoEmLink cor="inherit" onClick={() => void desfazer()}>
        Desfazer
      </AcaoEmLink>
    );
  } else if (removidaDoAviso) {
    acaoDoAviso = (
      <AcaoEmLink cor="inherit" onClick={() => void desfazerRemocao(removidaDoAviso)}>
        Desfazer
      </AcaoEmLink>
    );
  }

  const removidaUnica = aRemover?.length === 1 ? aRemover[0] : null;
  const tituloDaRemocao = removidaUnica
    ? `Remover ${nomeDe(removidaUnica)} ${referencia('de', destino.nivel, destino.nome)}?`
    : `Remover ${aRemover?.length ?? 0} ${config.rotulos.plural} ${referencia('de', destino.nivel, destino.nome)}?`;
  const textoDaRemocao = aRemover ? config.textoDeRemocao(aRemover, { destino, deCima: deCimaDe(aRemover) }) : [];

  return (
    <Box>
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => void reler()}>Tentar de novo</Button>}>
          {erro}
        </Alert>
      )}

      {listaUnica && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
            <TextField size="small" label="Buscar pelo nome" value={busca} onChange={(e) => setBusca(e.target.value)} />
            {config.filtrosRapidos?.map((filtro) => {
              const quantos = (efetivos?.proprias ?? []).filter(filtro.aceita).length;
              return (
                <Chip
                  key={filtro.id}
                  label={`${filtro.rotulo} ${quantos}`}
                  color={filtroRapido === filtro.id ? 'primary' : 'default'}
                  variant={filtroRapido === filtro.id ? 'filled' : 'outlined'}
                  onClick={() => setFiltroRapido(filtroRapido === filtro.id ? null : filtro.id)}
                />
              );
            })}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {filtrosDeOrigem.map((filtro) => (
              <Chip
                key={filtro.id}
                label={`${filtro.rotulo} ${filtro.quantos}`}
                color={origemFiltrada === filtro.id ? 'primary' : 'default'}
                variant={origemFiltrada === filtro.id ? 'filled' : 'outlined'}
                onClick={() => setOrigemFiltrada(filtro.id)}
              />
            ))}
          </Stack>
          <DataTable
            embedded
            columns={colunasDaLista}
            rows={linhasDaLista}
            getRowId={idDaLinha}
            loading={efetivos === null && !erro}
            emptyState={{
              title: `Nada aplicado a ${esteDestino}`,
              description: `Aqui aparece tudo o que vale para ${esteDestino}: o que foi aplicado nele e o que vem da empresa, da unidade ou do setor, com a origem de cada um.`,
            }}
            filtersActive={filtrosAtivos || origemFiltrada !== 'todos'}
            onClearFilters={limparFiltros}
            actions={acoesDaLista}
            selection={
              !podeEditar && !podeRemover
                ? undefined
                : {
                    selected: selecionados,
                    // Só as próprias se marcam: as de cima não se editam nem se removem daqui.
                    onChange: (ids) => setSelecionados(ids.filter((id) => idsDasProprias.has(String(id)))),
                    actions: acoesDaSelecao,
                  }
            }
          />
        </>
      )}

      {!listaUnica && (
        <Box component="section" aria-label={`Tabela de ${config.rotulos.plural} ${referenciaCurta('de', destino.nivel, destino.nome)}`}>
          {acimaDaTabela}
          {aviso && (
            <Alert severity="info" sx={{ mb: 2 }} action={acaoDoAviso} onClose={() => setAviso(null)}>
              {aviso.mensagem}
            </Alert>
          )}
          <DataTable
            embedded
            columns={colunasDoInventario}
            rows={linhasDoInventario}
            getRowId={idDaLinha}
            highlightRow={(linha) => !ehHerdada(linha) && recenteDe(linha)?.como === 'adicionado'}
            rowTone={(linha) => (ehHerdada(linha) ? 'muted' : recenteDe(linha)?.como === 'ajustado' ? 'info' : null)}
            loading={efetivos === null && !erro}
            emptyState={
              somenteLeitura
                ? {
                    title: `${f('Nenhum')} ${config.rotulos.singular} vale para ${destino.nome}`,
                    description: `Quando ${esteDestino}, o setor ou a empresa tiverem ${config.rotulos.plural}, aparecem aqui, com a origem de cada um.`,
                  }
                : {
                    title: `${f('Nenhum')} ${config.rotulos.singular} vale para ${destino.nome} ainda`,
                    description: `Use “Incluir ${config.rotulos.plural}” para começar. O que vier do setor ou da empresa também aparece aqui.`,
                  }
            }
          />
        </Box>
      )}

      <Dialog open={aRemover !== null} onClose={() => !removendo && setARemover(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{tituloDaRemocao}</DialogTitle>
        <DialogContent>
          {textoDaRemocao.map((paragrafo) => (
            <DialogContentText key={paragrafo} sx={{ mb: 1 }}>
              {paragrafo}
            </DialogContentText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setARemover(null)} disabled={removendo}>
            Manter
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmarRemocao()}
            disabled={removendo}
            sx={{ bgcolor: 'error.dark', '&:hover': { bgcolor: 'error.main' } }}
          >
            {removidaUnica ? `Remover ${config.rotulos.singular}` : `Remover ${config.rotulos.plural}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emLote !== null} onClose={() => !gravandoLote && setEmLote(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{emLote?.acao.rotulo}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {`Vale para ${contar(selecionadas.length, `${config.rotulos.singular} ${f('marcado')}`, `${config.rotulos.plural} ${f('marcados')}`)}; a mudança é gravada na hora.`}
          </DialogContentText>
          {emLote && emLote.acao.escolher(emLote.valor, (novo) => setEmLote({ ...emLote, valor: novo }))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmLote(null)} disabled={gravandoLote}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => emLote && void executarEmLote(emLote)}
            disabled={gravandoLote || !emLote || escolhaVazia(emLote.valor)}
          >
            {gravandoLote ? 'Aplicando…' : 'Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>

      {JanelaDeDetalhe && emDetalhe && (
        <JanelaDeDetalhe
          aberta
          aplicacao={emDetalhe}
          destino={destino}
          caminho={caminhoDoDestino}
          aoFechar={() => setEmDetalhe(null)}
          aoSalvar={() => {
            const conferida = String(config.aplicacao.id(emDetalhe));
            setConferidasNaJanela((antes) => new Set(antes).add(conferida));
            setEmDetalhe(null);
            aoMudarGravacaoRef.current?.({ tipo: 'salvo', em: new Date() });
            void reler();
          }}
        />
      )}

      <Snackbar
        open={listaUnica && aviso !== null}
        autoHideDuration={aviso?.desfazer ? 10000 : 5000}
        onClose={(_e, razao) => razao !== 'clickaway' && setAviso(null)}
        message={aviso?.mensagem}
        action={
          aviso?.desfazer ? (
            <AcaoEmLink cor="inherit" onClick={() => void desfazer()}>
              Desfazer
            </AcaoEmLink>
          ) : undefined
        }
      />
    </Box>
  );
}
